import type { SessionSummary } from '@/features/sessions/types/session.types'
import { httpClient } from '@/shared/api/httpClient'
import type { PageResponse } from '@/shared/api/PageResponse'

import type {
  EventSeries,
  EventSeriesPayload,
  SeriesOccurrence,
  StoreEventPayload,
} from '../types/event.types'
import type {
  AddVipPayload,
  FollowedVenue,
  PublicVenue,
  StoreAccount,
  StoreAccountPayload,
  StoreCampaignPayload,
  StoreCampaignResponse,
  VenueFollowStatus,
  StoreVenue,
  StoreVenuePayload,
  StoreVenueZone,
  StoreVenueZonePayload,
  StoreVip,
  UserVipMembership,
  VenueAgenda,
  VenueStats,
  VenueClosure,
  VenueClosurePayload,
  VenueDirectoryItem,
  VipUserSearchItem,
  ZoneAvailability,
} from '../types/store.types'

export const storeApi = {
  // ---------- Cuenta (nivel 1) ----------

  /** POST /store/register — alta "Soy una tienda" (auth USER). Crea la
   *  cuenta en PENDING y concede el rol TIENDA al owner. */
  registerAccount: (payload: StoreAccountPayload) =>
    httpClient.post<StoreAccount>('/store/register', payload).then((r) => r.data),

  /** GET /store/me — cuenta propia (rol TIENDA). */
  getMyAccount: () => httpClient.get<StoreAccount>('/store/me').then((r) => r.data),

  /** PUT /store/me — edita la cuenta propia (rol TIENDA). */
  updateMyAccount: (payload: StoreAccountPayload) =>
    httpClient.put<StoreAccount>('/store/me', payload).then((r) => r.data),

  /** DELETE /store/me — da de baja la cuenta de tienda: cancela eventos futuros,
   *  borra el negocio y baja el rol a USER. 204. Tras esto hay que hacer logout
   *  (el token TIENDA queda obsoleto). */
  closeMyAccount: () => httpClient.delete('/store/me').then(() => undefined),

  /** POST /store/me/logo (multipart 'file') — devuelve la cuenta completa ya
   *  actualizada (mismo procesado que las imágenes del hero — ver
   *  `HeroImageService`). */
  uploadLogo: (file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return httpClient.post<StoreAccount>('/store/me/logo', fd).then((r) => r.data)
  },

  // ---------- Locales (nivel 2) ----------

  /** GET /store/me/venues — locales propios (rol TIENDA). */
  listVenues: () => httpClient.get<StoreVenue[]>('/store/me/venues').then((r) => r.data),

  /** GET /store/me/venues/{id} — ficha de un local propio. */
  getVenue: (id: number) =>
    httpClient.get<StoreVenue>(`/store/me/venues/${id}`).then((r) => r.data),

  /** POST /store/me/venues — alta de un local; exige cuenta APPROVED (403
   *  `error.store.access.denied` si no). Queda PENDING. */
  createVenue: (payload: StoreVenuePayload) =>
    httpClient.post<StoreVenue>('/store/me/venues', payload).then((r) => r.data),

  /** PUT /store/me/venues/{id} — edita un local propio. */
  updateVenue: (id: number, payload: StoreVenuePayload) =>
    httpClient.put<StoreVenue>(`/store/me/venues/${id}`, payload).then((r) => r.data),

  /** DELETE /store/me/venues/{id}. */
  deleteVenue: (id: number) => httpClient.delete(`/store/me/venues/${id}`).then(() => undefined),

  // ---------- Carteleras de un local (nivel 3) ----------

  /** GET /store/me/venues/{id}/campaigns — carteleras propias del local. */
  listVenueCampaigns: (venueId: number) =>
    httpClient
      .get<StoreCampaignResponse[]>(`/store/me/venues/${venueId}/campaigns`)
      .then((r) => r.data),

  /** POST /store/me/venues/{id}/campaigns — crea una cartelera del local;
   *  exige local APPROVED (403 `error.venue.access.denied` si no). Queda
   *  PENDING. */
  createVenueCampaign: (venueId: number, payload: StoreCampaignPayload) =>
    httpClient
      .post<StoreCampaignResponse>(`/store/me/venues/${venueId}/campaigns`, payload)
      .then((r) => r.data),

  /** PUT /store/me/venues/{id}/campaigns/{cid} — edita; si estaba APPROVED
   *  vuelve a PENDING. */
  updateVenueCampaign: (venueId: number, campaignId: number, payload: StoreCampaignPayload) =>
    httpClient
      .put<StoreCampaignResponse>(`/store/me/venues/${venueId}/campaigns/${campaignId}`, payload)
      .then((r) => r.data),

  /** DELETE /store/me/venues/{id}/campaigns/{cid}. */
  deleteVenueCampaign: (venueId: number, campaignId: number) =>
    httpClient.delete(`/store/me/venues/${venueId}/campaigns/${campaignId}`).then(() => undefined),

  /** POST /store/me/venues/{id}/campaigns/image (multipart 'file') — mismo
   *  procesado que `/admin/hero-campaigns/image` (misma `HeroImageService`).
   *  Devuelve solo la URL: la imagen no se persiste hasta guardar el form. */
  uploadVenueCampaignImage: (venueId: number, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return httpClient
      .post<{ url: string }>(`/store/me/venues/${venueId}/campaigns/image`, fd)
      .then((r) => r.data)
  },

  /** POST /store/me/events/cover (multipart 'file') — portada propia (póster 3:4)
   *  de un evento de tienda sin juego. Devuelve la URL; no se persiste hasta crear
   *  el evento/serie. */
  uploadEventCover: (file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return httpClient.post<{ url: string }>(`/store/me/events/cover`, fd).then((r) => r.data)
  },

  // ---------- Zonas de juego de un local (nivel 3) ----------

  /** GET /store/me/venues/{id}/zones — zonas de juego propias del local. */
  listVenueZones: (venueId: number) =>
    httpClient.get<StoreVenueZone[]>(`/store/me/venues/${venueId}/zones`).then((r) => r.data),

  /** POST /store/me/venues/{id}/zones — crea una zona; tipo duplicado → 409
   *  `error.store.zone.type.already.exists`. */
  createVenueZone: (venueId: number, payload: StoreVenueZonePayload) =>
    httpClient
      .post<StoreVenueZone>(`/store/me/venues/${venueId}/zones`, payload)
      .then((r) => r.data),

  /** PUT /store/me/venues/{id}/zones/{zoneId} — edita (en la práctica solo el
   *  nº de mesas; el tipo es la identidad de la zona). */
  updateVenueZone: (venueId: number, zoneId: number, payload: StoreVenueZonePayload) =>
    httpClient
      .put<StoreVenueZone>(`/store/me/venues/${venueId}/zones/${zoneId}`, payload)
      .then((r) => r.data),

  /** DELETE /store/me/venues/{id}/zones/{zoneId}. */
  deleteVenueZone: (venueId: number, zoneId: number) =>
    httpClient.delete(`/store/me/venues/${venueId}/zones/${zoneId}`).then(() => undefined),

  /** GET /store/me/venues/{id}/closures — días de cierre/vacaciones del local. */
  listVenueClosures: (venueId: number) =>
    httpClient.get<VenueClosure[]>(`/store/me/venues/${venueId}/closures`).then((r) => r.data),

  /** POST /store/me/venues/{id}/closures — añade un cierre; fin < inicio → 400. */
  createVenueClosure: (venueId: number, payload: VenueClosurePayload) =>
    httpClient
      .post<VenueClosure>(`/store/me/venues/${venueId}/closures`, payload)
      .then((r) => r.data),

  /** DELETE /store/me/venues/{id}/closures/{closureId}. */
  deleteVenueClosure: (venueId: number, closureId: number) =>
    httpClient.delete(`/store/me/venues/${venueId}/closures/${closureId}`).then(() => undefined),

  // ---------- Agenda del owner (partidas de sus locales) ----------

  /** GET /store/me/agenda — próximas partidas ACTIVAS de todos los locales
   *  del owner, ordenadas por fecha, con la ocupación de su zona (rol TIENDA). */
  getMyAgenda: () => httpClient.get<VenueAgenda[]>('/store/me/agenda').then((r) => r.data),

  /** GET /store/me/stats — estadísticas de ocupación de los locales del owner. */
  getMyStats: () => httpClient.get<VenueStats[]>('/store/me/stats').then((r) => r.data),

  /** DELETE /store/me/venues/{venueId}/agenda/{sessionId} — quita una partida
   *  del tablero del local: libera la mesa + desvincula + bloquea el re-vínculo. 204. */
  removeFromAgenda: (venueId: number, sessionId: number) =>
    httpClient.delete(`/store/me/venues/${venueId}/agenda/${sessionId}`).then(() => undefined),

  // ---------- Eventos de tienda (torneos/eventos) ----------

  /** POST /store/me/events — crea un evento suelto en un local propio. 201 con el detalle. */
  createEvent: (payload: StoreEventPayload) =>
    httpClient.post<SessionSummary>('/store/me/events', payload).then((r) => r.data),

  /** GET /store/me/events — eventos del owner (kind != PLAYER_GAME), recientes primero. */
  listEvents: () => httpClient.get<SessionSummary[]>('/store/me/events').then((r) => r.data),

  /** GET /store/me/event-series — series recurrentes del owner. */
  listEventSeries: () =>
    httpClient.get<EventSeries[]>('/store/me/event-series').then((r) => r.data),

  /** POST /store/me/event-series — crea una serie recurrente. */
  createEventSeries: (payload: EventSeriesPayload) =>
    httpClient.post<EventSeries>('/store/me/event-series', payload).then((r) => r.data),

  /** PUT /store/me/event-series/{id} — edita la plantilla de una serie. */
  updateEventSeries: (id: number, payload: EventSeriesPayload) =>
    httpClient.put<EventSeries>(`/store/me/event-series/${id}`, payload).then((r) => r.data),

  /** DELETE /store/me/event-series/{id} — desactiva la serie (deja de materializar). 204. */
  deactivateEventSeries: (id: number) =>
    httpClient.delete(`/store/me/event-series/${id}`).then(() => undefined),

  /** GET /store/me/event-series/{id}/occurrences — ocurrencias futuras de la serie. */
  listSeriesOccurrences: (id: number) =>
    httpClient
      .get<SeriesOccurrence[]>(`/store/me/event-series/${id}/occurrences`)
      .then((r) => r.data),

  /** DELETE /store/me/event-series/{id}/permanent — borra una serie inactiva. 204
   *  (400 si sigue activa). */
  deleteEventSeriesPermanent: (id: number) =>
    httpClient.delete(`/store/me/event-series/${id}/permanent`).then(() => undefined),

  // ---------- VIP de tienda (owner) ----------

  /** GET /store/me/vips?page=&size=&q= — lista paginada de VIP (owner-only),
   *  filtrable por nick o email. */
  listVips: (page = 0, q = '', size = 5) =>
    httpClient
      .get<PageResponse<StoreVip>>('/store/me/vips', { params: { page, size, q } })
      .then((r) => r.data),

  /** GET /store/me/vips/search?q= — busca usuarios por nick para el selector
   *  (owner-only; devuelve id+nick+avatar). Vacío si `q` en blanco. */
  searchVipUsers: (q: string) =>
    httpClient
      .get<VipUserSearchItem[]>('/store/me/vips/search', { params: { q } })
      .then((r) => r.data),

  /** POST /store/me/vips — añade un VIP (o actualiza su caducidad si ya lo era). 201. */
  addVip: (payload: AddVipPayload) =>
    httpClient.post<StoreVip>('/store/me/vips', payload).then((r) => r.data),

  /** PATCH /store/me/vips/{id} — cambia la caducidad (null = permanente). */
  updateVipExpiry: (id: number, expiresAt: string | null) =>
    httpClient.patch<StoreVip>(`/store/me/vips/${id}`, { expiresAt }).then((r) => r.data),

  /** POST /store/me/vips/{id}/extend — suma un mes a la caducidad. */
  extendVip: (id: number) =>
    httpClient.post<StoreVip>(`/store/me/vips/${id}/extend`).then((r) => r.data),

  /** DELETE /store/me/vips/{id} — quita el VIP. 204. */
  removeVip: (id: number) => httpClient.delete(`/store/me/vips/${id}`).then(() => undefined),

  // ---------- VIP (usuario) ----------

  /** GET /me/vips — tiendas donde el usuario actual es VIP activo (insignia). */
  getMyVipMemberships: () => httpClient.get<UserVipMembership[]>('/me/vips').then((r) => r.data),

  // ---------- Público (directorio + ficha de local) ----------

  /** GET /venues?provinceCode=&cityCode= — directorio de locales (solo APPROVED). */
  directory: (provinceCode?: string, cityCode?: string) =>
    httpClient
      .get<VenueDirectoryItem[]>('/venues', { params: { provinceCode, cityCode } })
      .then((r) => r.data),

  /** PUT /me/followed-venues/{venueId} — seguir a un local (idempotente). */
  followVenue: (venueId: number) =>
    httpClient.put<VenueFollowStatus>(`/me/followed-venues/${venueId}`).then((r) => r.data),

  /** DELETE /me/followed-venues/{venueId} — dejar de seguir (idempotente). */
  unfollowVenue: (venueId: number) =>
    httpClient.delete<VenueFollowStatus>(`/me/followed-venues/${venueId}`).then((r) => r.data),

  /** GET /me/followed-venues — locales que sigue el usuario. */
  listFollowedVenues: () =>
    httpClient.get<FollowedVenue[]>('/me/followed-venues').then((r) => r.data),

  /** GET /venues/{slug} — ficha pública de un local (404 si no existe o no está APPROVED). */
  getPublic: (slug: string) =>
    httpClient.get<PublicVenue>(`/venues/${encodeURIComponent(slug)}`).then((r) => r.data),

  /** GET /venues/{slug}/sessions — próximas partidas ACTIVAS del local, para
   *  el bloque "próximas partidas aquí" de la ficha pública (permitAll). */
  getPublicUpcomingSessions: (slug: string) =>
    httpClient
      .get<SessionSummary[]>(`/venues/${encodeURIComponent(slug)}/sessions`)
      .then((r) => r.data),

  /** GET /venues/zones/{zoneId}/availability?date=YYYY-MM-DD — mesas libres de
   *  una zona en una fecha (la ocupación es por día). La usa el paso «Cuándo» de
   *  crear partida tras elegir la fecha (permitAll). */
  getZoneAvailability: (zoneId: number, date: string) =>
    httpClient
      .get<ZoneAvailability>(`/venues/zones/${zoneId}/availability`, { params: { date } })
      .then((r) => r.data),
}
